import RadioButtonChecked from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import ImageList, { type ImageListProps } from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { EvidenceImage } from '../EvidenceImage/EvidenceImage';
import type { PhotoEvidenceItem } from '../PhotoEvidenceList/PhotoEvidenceList';

type PhotoEvidenceSelectItem = PhotoEvidenceItem & { id: number };

export type PhotoEvidenceMultiSelectListProps = {
  evidencePhotos: PhotoEvidenceSelectItem[];
  emptyStateLabel?: ReactNode;
  direction?: 'row' | 'column';
  slotProps?: {
    imageList?: Omit<ImageListProps, 'children'>;
  };
  selected?: number[];
  onSelectionChange?: (selected: number[]) => void;
};

export const PhotoEvidenceMultiSelectList = (props: PhotoEvidenceMultiSelectListProps) => {
  const { evidencePhotos, slotProps, direction } = props;
  const { isMobile } = useBreakpoint();

  const [isSelected, setSelected] = useState<number[]>([]);
  const selected = props.selected ?? isSelected;
  const updateSelected = (newSelected: number[]) => {
    if (props.onSelectionChange) {
      props.onSelectionChange(newSelected);
    }
    setSelected(newSelected);
  };

  if (!evidencePhotos.length) {
    return props.emptyStateLabel ? <Typography>{props.emptyStateLabel}</Typography> : null;
  }

  const handleSelect = (photo: PhotoEvidenceSelectItem) => {
    const { id } = photo;
    if (selected.includes(id)) {
      updateSelected(selected.filter((s) => s !== id));
    } else {
      updateSelected([...selected, id]);
    }
  };

  return (
    <ImageList
      gap={isMobile ? 10 : 20}
      cols={isMobile ? 2 : 4}
      {...slotProps?.imageList}
      sx={{
        gridAutoFlow: direction === 'row' ? 'column' : 'row',
        my: 0,
        ...(direction === 'row' && {
          gridTemplateColumns: 'auto !important',
          gridTemplateRows: `repeat(${slotProps?.imageList?.cols ?? 2}, 1fr)`,
        }),
      }}
    >
      {evidencePhotos.map((photo, index) => {
        const { id, thumbnailSrc, placeholderSrc, src, isLoading, uploadProgress, cols, rows } = photo;
        const isSelected = selected.includes(id);

        return (
          <ImageListItem
            key={src + index}
            sx={(theme) => ({
              border: isSelected ? '4px solid' : 'none',
              borderColor: theme.palette.primary.main,
              borderRadius: 3,
              cursor: 'pointer',
            })}
            onClick={() => handleSelect(photo)}
            cols={cols}
            rows={rows}
          >
            <EvidenceImage
              src={thumbnailSrc || src}
              loading="lazy"
              placeholderSrc={placeholderSrc}
              isLoading={isLoading}
              uploadProgress={uploadProgress}
            />

            <ImageListItemBar
              position="top"
              sx={{
                background: 'transparent',
                p: 1,
              }}
              actionIcon={
                isSelected ? (
                  <RadioButtonChecked data-testid="selected-indicator" color="primary" />
                ) : (
                  <RadioButtonUnchecked data-testid="unselected-indicator" color="primary" />
                )
              }
              actionPosition="right"
            />
          </ImageListItem>
        );
      })}
    </ImageList>
  );
};
